import React, { useState, useRef, ReactElement, useEffect } from 'react';
import MasterConfig from '../../../config/Master';
import styled from '@emotion/styled';
import { css } from 'glamor';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../../atoms/buttons/Button';
import Loader from '../../../atoms/LoaderStyled';
import { getBackgroundTheme, getPatternModal, getTextColor } from '../../../style';
import { areEqual, isDarkMode } from '../../../utils/helpers';
import AddContentIcon from '../../../atoms/icons/AddContentIcon';
import { Add } from '@material-ui/icons';
import { VideoConfig, PhotoConfig } from '../../../config/Media';
import CaptureMedia from '../CaptureMedia';
import UploadMediaAgreementModal from '../../modals/UploadMediaAgreementModal';
import TrackLoadingToMixpanel from '../../../components/common/TrackLoadingToMixPanel';
import GalleryUploadOption from './GalleryUploadOption/GalleryUploadOption';
import { PhotoListTypes, MediaTypes } from '../constants';
import GalleryFileUploadedContainer from './GalleryFileUploader/GalleryFileUploadedContainer';
import { handleUploadGAlleryVysionEvent } from '../../../utils/vysion/profile/uploadGallery';
import { ProfileUploadGalleryClickVysionEventsEnum } from '../../../utils/vysion/profile/uploadGallery/click';
import { useDisableBodyElementScroll } from '../../../hooks/useDisableBodyScroll';
import UnsupportedFileTypeErrorModal from '../../modals/UnsupportedFileTypeErrorModal';
import { displayFailMessage } from '../../../actions/notificationActions';
import { addNewPhoto, clearPendingPhotosUpload } from '../../../actions/profileActions';
import { addNewMedia } from '../../../actions/galleryActions';
import { RootState } from '../../../store';
import { isMobile } from 'react-device-detect';

const StyledDropzone = styled('div')({
  boxSizing: 'border-box',
  cursor: 'pointer',
  width: '100%',
});

const PhotoItem = styled('div')(
  isRelative => ({
    boxSizing: 'border-box',
    float: 'left',
    width: '100%',
    height: '100%',
    border: isDarkMode() ? '0' : '1px solid #2B8FD7',
    background: isDarkMode() ? (isMobile ? '#212121' : '#323232') : '#EAF4FB',
    borderRadius: 6,
    cursor: 'pointer',
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: isRelative ? 'relative' : 'inherit',
  })
  // ({ isRelative }) => (isRelative ? { position: 'relative' } : null)
);

const PhotoItemWrapper = styled('div')({
  position: 'absolute',
  top: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
});

const PhotoItemLabel = styled('div')({
  display: 'block',
  fontWeight: 500,
  lineHeight: '20px',
  fontSize: 14,
});

const Uploader = styled(StyledDropzone)`
  &:hover,
  &:hover button {
    color: ${isDarkMode() ? '#2B8FD7' : '#71b5e4'} !important;
  }

  &:hover svg,
  &:hover svg path,
  &:hover button svg path {
    fill: ${isDarkMode() ? '#2B8FD7' : '#71b5e4'} !important;
    color: ${isDarkMode() ? '#2B8FD7' : '#71b5e4'} !important;
    stroke: ${isDarkMode() ? '#2B8FD7' : '#71b5e4'} !important;
  }
`;

interface GalleryUploadProps {
  clearBulkSelection: any;
  showAddContentText: boolean;
  maxUpload: number;
}

const GalleryUpload = ({
  showAddContentText = true,
  clearBulkSelection,
  maxUpload = MasterConfig.MAX_NUMBER_PHOTOS,
}: GalleryUploadProps): ReactElement => {
  const dispatch = useDispatch();
  const uploadSnackbar = useSelector((state: RootState) => state.uploadSnackbar);
  const { pendingUploads: isUploading } = useSelector((state: RootState) => state.profile);
  const uploaderRef = useRef(null);
  const [fileList, setFileList] = useState<PhotoListTypes | null>(null);
  const [showConfirmUploadModal, setShowConfirmUploadModal] = useState(false);
  const [showUploadOption, setShowUploadOption] = useState(false);
  const [useCaptureMedia, setUseCaptureMedia] = useState(false);
  const [showUploadReview, setShowUploadReview] = useState(false);
  const [mediaType, setMediaType] = useState<MediaTypes>('photo');
  const [isUploadedFromQA, setIsUploadedFromQA] = useState(false);
  const [showUnsupportedFileModal, setShowUnsupportedFileModal] = useState(false);
  const [filename, setFilename] = useState('');

  useDisableBodyElementScroll(showUploadReview);

  useEffect(() => {
    dispatch(clearPendingPhotosUpload());
  }, []);

  const handleFileChange = async (selectedFiles: FileList | null) => {
    if (!selectedFiles) {
      dispatch(
        displayFailMessage({
          info: `Error uploading ${mediaType} no file to upload. Please try again or `,
          linkInfo: 'contact support.',
          url: `${MasterConfig.FULL_URL}/faq/giving-feedback`,
          newWindow: true,
        })
      );
      setFileList(null);
      return;
    }

    const allSelectedFiles = Array.from(selectedFiles);

    for (let index = 0; index < allSelectedFiles.length; index++) {
      const selectedFile = allSelectedFiles[index];

      if (mediaType === 'photo' && !PhotoConfig.allowedMimeType.includes(selectedFile.type)) {
        setFilename(selectedFile.name);
        setShowUnsupportedFileModal(true);
        return;
      }

      if (mediaType === 'video' && !VideoConfig.allowedMimeType.includes(selectedFile.type)) {
        setFilename(selectedFile.name);
        setShowUnsupportedFileModal(true);
        return;
      }
    }

    setShowUploadOption(false);
    setShowUploadReview(true);
    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    setFileList(allSelectedFiles);
  };

  const handleSubmitQAPhoto = async () => {
    setIsUploadedFromQA(true);
    setShowUploadOption(true);

    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    setFileList(document.getElementById('upload-qa-photo').files);
  };

  const resetState = () => {
    setFileList(null);
    setMediaType('photo');
    setUseCaptureMedia(false);
    if (uploaderRef && uploaderRef.current) {
      uploaderRef.current.value = null;
    }
  };

  const toggleSwitch = option => {
    setMediaType(option);
  };

  const addContent = () => {
    /**
     * don't allow upload when there active uploads.
     * which is true if `uploadSnackbar.show` is truthy
     * component GalleryUploadBackgroundProgress.tsx
     *
     * and don't allow upload when user has uploaded maximum gallery already
     */
    if (maxUpload >= MasterConfig.MAX_NUMBER_PHOTOS || uploadSnackbar.show) {
      return;
    }

    // add check before triggering event
    // because for some reason, when clicking "choose existing" button
    // this is being triggered again
    if (!showUploadOption) {
      handleUploadGAlleryVysionEvent({
        event: ProfileUploadGalleryClickVysionEventsEnum.PROFILE_UPLOAD_GALLERY_ADD_CONTENT,
      });
    }

    handleShowAgreement();

    // Make sure upload process is reset
    setFileList(null);
    setUseCaptureMedia(false);
  };

  const handleAddPhoto = (imageFile, isPrivate = false) => {
    dispatch(addNewPhoto(imageFile, isPrivate));
    resetState();
  };

  const handleAddVideo = (media, isPrivate = false, mediaType) => {
    dispatch(addNewMedia(media, isPrivate, mediaType));

    resetState();
  };

  const handleShareMedia = (media, type, isPrivate = false) => {
    return media.media.then(res => {
      if (type === 'photo') {
        return handleAddPhoto(res.blob, isPrivate);
      }

      if (type === 'video') {
        return handleAddVideo(res.blob, isPrivate, 'video/mp4');
      }
    });
  };

  const handleConfirmUpload = () => {
    setShowConfirmUploadModal(false);

    // Programatically trigger dropzone click event
    uploaderRef && uploaderRef.current && uploaderRef.current.click();
  };

  const handleShowAgreement = () => {
    if (localStorage.getItem('wyp_gallery_agreement') !== 'true') {
      setShowConfirmUploadModal(true);
    } else {
      onConfirmAgreement(true);
    }

    clearBulkSelection();
  };

  const onConfirmAgreement = alwasyShow => {
    setShowConfirmUploadModal(false);
    setShowUploadOption(true);

    localStorage.setItem('swuiMediaType', 'photo');

    if (alwasyShow === false && localStorage.getItem('wyp_gallery_agreement') !== 'true') {
      localStorage.setItem('wyp_gallery_agreement', 'true');
    }
  };

  const renderUploader = (isFetching, disabled = false) => {
    if (uploadSnackbar.show) {
      return null;
    }

    return (
      <>
        <div style={{ display: 'none' }}>
          <form>
            <input multiple type="file" name="file" id="upload-qa-photo" data-test-id="upload-qa-photo" />
            <button id="submit-qa-photo" data-test-id="submit-qa-photo" type="button" onClick={handleSubmitQAPhoto}>
              Submit
            </button>
          </form>
        </div>
        {/* @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389 */}
        <PhotoItem isRelative onClick={addContent} data-test-a="PhotoItem" style={{ paddingTop: '130%' }}>
          <PhotoItemWrapper>
            <Loader
              active={isFetching}
              background={getBackgroundTheme().gallery}
              data-test-a="1"
              base={{ height: '100%' }}
            >
              {isFetching && <TrackLoadingToMixpanel page="Profile - loading new uploaded media" />}
              <Uploader>
                {!disabled ? (
                  <div className="add-content-box">
                    {mediaType && (
                      <input
                        type="file"
                        name="gallery-upload"
                        id="ugallery-upload"
                        data-test-id="gallery-upload"
                        ref={uploaderRef}
                        style={{ display: 'none' }}
                        onChange={e => {
                          handleFileChange(e.target.files);
                        }}
                        accept={mediaType === 'photo' ? PhotoConfig.allowedMimeType : VideoConfig.allowedMimeType}
                        multiple
                        min={1}
                        max={2}
                      />
                    )}
                    <AddContentIcon cssStyle={{ marginBottom: 5 }} fillColor={getPatternModal().cancelBlue} />
                    <Button
                      buttonType="chromeless"
                      data-test-id="gallery-add-content"
                      customStyle={{
                        fontWeight: '500',
                        ...getTextColor().BLUE,
                        padding: 0,
                        width: '100%',
                        '&:hover': { color: `${getPatternModal().cancelBlue} !important` },
                      }}
                    >
                      <div style={{ display: 'inline-flex', alignItems: 'center', flexFlow: 'column' }}>
                        {showAddContentText ? (
                          <>
                            <Add
                              fontSize="large"
                              style={{
                                ...getTextColor().BLUE,
                                stroke: getPatternModal().cancelBlue,
                                strokeWidth: '10%',
                              }}
                            />
                            <PhotoItemLabel>Add Content</PhotoItemLabel>
                          </>
                        ) : null}
                      </div>
                    </Button>
                  </div>
                ) : (
                  <div className={`${css({ display: 'table', height: '100%' })}`}>
                    <div
                      className={`${css({
                        display: 'table-cell',
                        verticalAlign: 'middle',
                        fontSize: 14,
                        padding: '0 5px',
                      })}`}
                    >
                      You have reached your maximum number of photos.{' '}
                    </div>
                  </div>
                )}
              </Uploader>
            </Loader>
          </PhotoItemWrapper>
        </PhotoItem>
      </>
    );
  };

  const handleUploadOption = (type, mediaType) => {
    setShowConfirmUploadModal(false);
    setShowUploadOption(false);
    setMediaType(mediaType);

    if (type === 'capture') {
      // bypass swui media selected type
      localStorage.setItem('swuiMediaType', mediaType);
      setUseCaptureMedia(true);
      return;
    }

    if (isUploadedFromQA) {
      setShowUploadReview(true);
    } else {
      handleConfirmUpload();
    }
  };

  const handleUnsupportedFileTypeCloseModal = () => {
    setShowUnsupportedFileModal(false);
  };

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', overflow: 'hidden' }}>
      {renderUploader(
        isUploading ? isUploading.public || isUploading.private : false,
        Boolean(maxUpload >= MasterConfig.MAX_NUMBER_PHOTOS)
      )}
      {showConfirmUploadModal && (
        <UploadMediaAgreementModal
          isOpen={showConfirmUploadModal}
          onClose={() => setShowConfirmUploadModal(false)}
          onConfirm={alwasyShow => {
            onConfirmAgreement(alwasyShow);
          }}
        />
      )}

      <GalleryUploadOption
        isOpen={showUploadOption}
        onClose={() => {
          setShowUploadOption(false);
          setMediaType('photo');
        }}
        onSelected={handleUploadOption}
        uploadOption={mediaType}
        onToggle={toggleSwitch}
        requiredPublicPhoto={1}
      />

      {/* Todo: get existing media count */}
      {showUploadReview && fileList && (
        <GalleryFileUploadedContainer
          mediaType={mediaType}
          // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
          files={fileList}
          existingUploadedMediaCount={maxUpload}
          onUploadAgainClicked={() => {
            resetState();
            setShowUploadOption(true);
            setShowUploadReview(false);
          }}
          onClose={() => {
            handleUploadGAlleryVysionEvent({
              event: ProfileUploadGalleryClickVysionEventsEnum.PROFILE_UPLOAD_GALLERY_CONTENT_CANCEL,
            });
            setShowUploadReview(false);
            resetState();
          }}
        />
      )}
      {useCaptureMedia && (
        <CaptureMedia
          onClose={() => {
            setUseCaptureMedia(false);
            setShowUploadOption(true);
          }}
          onShare={handleShareMedia}
          isOpen={useCaptureMedia}
        />
      )}
      <UnsupportedFileTypeErrorModal
        isOpen={showUnsupportedFileModal}
        filename={filename}
        onUploadAnother={handleUnsupportedFileTypeCloseModal}
        onClose={handleUnsupportedFileTypeCloseModal}
        mediaType={mediaType}
      />
    </div>
  );
};

export default React.memo(GalleryUpload, areEqual);
